import { OptionCardGroup } from '@rocco/components/questionnaire';
import type { BuildHubOption } from '~/ui-rocco/hooks/use-build-hubs';

import type { QuestionnaireDefinition } from '../../questionnaire';
import type { MatchAnswers } from '../answers';
import type { GetMatchedOutcomeCopy } from '../GetMatchedQuestionnaire';
import {
    BUDGET_OPTIONS,
    PROJECT_TYPE_OPTIONS,
    USER_TYPE_OPTIONS,
} from '../options';
import { BriefStep } from '../steps/BriefStep';
import { LocationStep } from '../steps/LocationStep';
import { ProjectStep } from '../steps/ProjectStep';
import { ProjectSummaryStep } from '../steps/ProjectSummaryStep';

export interface ProductDefinitionOptions {
    paneImage?: string;
    /** Projects the reader already owns. Empty means the opening step never shows. */
    projects?: BuildHubOption[];
}

/** See the professional flow: the questions a project can answer outright. */
const answeredByProject = (answers: MatchAnswers): boolean =>
    answers.buildHubID !== null && answers.useProjectDetails;

/**
 * The product brief: five questions, in the order the design lays them out.
 *
 * Shorter than the professional flow by design — someone sourcing a product has
 * already narrowed the problem, and the questions that remain are about the
 * project the product goes into rather than the product itself. That is why the
 * budget question uses project-scale brackets.
 */
export const buildProductDefinition = ({
    paneImage,
    projects = [],
}: ProductDefinitionOptions = {}): QuestionnaireDefinition<MatchAnswers> => ({
    authPosition: 'first',
    id: 'get-matched-product',
    paneImage,
    progressLabel: 'Get Matched',
    title: 'Get Matched',
    steps: [
        {
            heading: 'What is this brief for?',
            isApplicable: () => projects.length > 0,
            isComplete: (answers) => answers.projectChoice !== null,
            key: 'project',
            microLabel: 'your project',
            subheading:
                'If it is about a project you already have, we can carry over what we know.',
            render: ({ answers, next, update }) => (
                <ProjectStep
                    answers={answers}
                    next={next}
                    projects={projects}
                    update={update}
                />
            ),
        },
        {
            heading: "Here's what we'll use",
            isApplicable: (answers) => answers.buildHubID !== null,
            key: 'projectSummary',
            microLabel: 'from your project',
            subheading: (answers) => {
                const chosen = projects.find(
                    (entry) => Number(entry.ID) === answers.buildHubID,
                );

                // Named outright. "Your project" is vague to anyone who has
                // more than one, and this is the screen where they agree to
                // details they are not being shown the source of otherwise.
                return chosen
                    ? `Straight from ${chosen.Title}, so you do not have to say it twice.`
                    : 'Straight from your project, so you do not have to say it twice.';
            },
            render: ({ answers, update }) => (
                <ProjectSummaryStep
                    answers={answers}
                    projects={projects}
                    update={update}
                />
            ),
        },
        {
            // "First" is only true when nothing was asked before it. A reader
            // with a project has already answered which one.
            heading:
                projects.length > 0
                    ? 'Tell us who you are.'
                    : 'First, tell us who you are.',
            isComplete: (answers) => Boolean(answers.userType),
            key: 'userType',
            microLabel: 'who you are',
            render: ({ answers, next, update }) => (
                <OptionCardGroup
                    label="Who you are"
                    onChange={(value) => {
                        update({ userType: value });
                        next();
                    }}
                    options={USER_TYPE_OPTIONS}
                    value={answers.userType}
                />
            ),
        },
        {
            heading: 'What type of project are you planning?',
            isApplicable: (answers) => !answeredByProject(answers),
            isComplete: (answers) => Boolean(answers.projectType),
            key: 'projectType',
            microLabel: 'project type',
            subheading: 'This helps us match products suited to your project.',
            render: ({ answers, next, update }) => (
                <OptionCardGroup
                    label="Project type"
                    onChange={(value) => {
                        update({ projectType: value });
                        next();
                    }}
                    options={PROJECT_TYPE_OPTIONS}
                    value={answers.projectType}
                />
            ),
        },
        {
            heading: "What's your project budget?",
            isApplicable: (answers) => !answeredByProject(answers),
            isComplete: (answers) => Boolean(answers.budget),
            key: 'budget',
            subheading:
                'A rough range is enough, it helps us match products in the right price bracket.',
            render: ({ answers, next, update }) => (
                <OptionCardGroup
                    label="Project budget"
                    onChange={(value) => {
                        update({ budget: value });
                        next();
                    }}
                    options={BUDGET_OPTIONS}
                    value={answers.budget}
                />
            ),
        },
        {
            heading: 'Where is your project based?',
            isApplicable: (answers) => !answeredByProject(answers),
            isComplete: (answers) => answers.region.trim().length > 0,
            key: 'region',
            subheading:
                'So we can match you with suppliers who deliver or offer pickup in your area.',
            render: ({ answers, update }) => (
                <LocationStep answers={answers} update={update} />
            ),
        },
        {
            columnWidth: 'wide',
            heading: 'Last step: tell us what you need',
            isComplete: (answers) => answers.description.trim().length > 0,
            key: 'brief',
            subheading:
                'The more detail the better. Include quantities, delivery timing, budget and anything else that matters to you.',
            render: ({ answers, update }) => (
                <BriefStep
                    answers={answers}
                    placeholder="e.g. We're after matte black tapware for a coastal-style kitchen — 4 mixers and 2 wall-mounted spouts. Open to NZ-made suppliers, delivered within 6 weeks, budget around $6K."
                    update={update}
                    uploadLabel="+ Add attachment"
                />
            ),
        },
    ],
});

export const buildProductOutcome = (): GetMatchedOutcomeCopy => ({
    actionLabel: 'Back to ArchiPro',
    body: "Our team is reviewing your brief and hand-picking suppliers whose products genuinely fit. We'll be in touch within 2 business days.",
    footnote:
        'Every match is quality-checked by our team — so you only hear from people worth your time.',
    heading: 'Request received',
    steps: [
        {
            description:
                'Usually within a day — a real person, not an algorithm.',
            title: 'We review your brief',
        },
        {
            description:
                'Suppliers whose products suit your project, style and budget.',
            title: 'We hand-pick your matches',
        },
        {
            description:
                'Directly, so you can start the conversation on your terms.',
            title: 'We introduce you',
        },
    ],
});
